export type ReportIdentitySource =
  | "gcp"
  | "sending_practice"
  | "submitting_lab"
  | "surgery_center";

export interface ReportIdentityHeader {
  name: string;
  medicalDirector: string | null;
  address: string | null;
  phone: string | null;
  fax: string | null;
  logoUrl: string | null;
}

export interface ClientReportIdentity {
  prefix: string | null;
  source: ReportIdentitySource;
  header: ReportIdentityHeader;
  footerHtml: string;
}

interface ReportParty extends ReportIdentityHeader {
  footerHtml?: string | null;
}

interface ResolveClientReportIdentityInput {
  caseNumber: string;
  facility: ReportIdentityHeader;
  practice: ReportParty;
  ihcGeneralComment?: string | null;
}

export const GCP_STANDARD_REPORT_HEADER = {
  name: "Gulf Coast Pathologists",
  medicalDirector: "Dr. Nick Lancia, MD",
  address: "2631 McCormick Dr. Ste 104, Clearwater, FL 33759",
  phone: "(727) 842-4848",
  fax: "(727) 842-9513",
} as const;

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

export function getReportCasePrefix(caseNumber?: string | null): string | null {
  const match = caseNumber
    ?.trim()
    .toUpperCase()
    .match(/^([A-Z]+)(?:\d|[-_])/);
  return match?.[1] || null;
}

function buildGcpFooterHtml(ihcGeneralComment?: string | null): string {
  const location = "2631 McCormick Dr, Suite 104, Clearwater, FL 33759";
  const lines = [
    `TC: unless otherwise specified, performed by Gulf Coast Pathologists (CLIA #10D 0717479) ${location}.`,
    `PC: unless otherwise specified, performed by Gulf Coast Pathologists (CLIA #10D 0717479) ${location} or location 1.`,
    ihcGeneralComment?.trim() || null,
  ].filter((line): line is string => !!line);

  return `<div class="report-footer">${lines
    .map((line) => `<p>${escapeHtml(line)}</p>`)
    .join("")}</div>`;
}

export function resolveClientReportIdentityFallback(
  input: ResolveClientReportIdentityInput
): ClientReportIdentity {
  const prefix = getReportCasePrefix(input.caseNumber);
  const useGcp =
    prefix === "S" || prefix === "G" || !input.practice.name?.trim();
  if (!useGcp) {
    return {
      prefix,
      source: "sending_practice",
      header: {
        name: input.practice.name,
        medicalDirector: input.practice.medicalDirector,
        address: input.practice.address,
        phone: input.practice.phone,
        fax: input.practice.fax,
        logoUrl: input.practice.logoUrl,
      },
      footerHtml: input.practice.footerHtml || "",
    };
  }

  return {
    prefix,
    source: "gcp",
    header: {
      ...GCP_STANDARD_REPORT_HEADER,
      logoUrl: input.facility.logoUrl,
    },
    footerHtml: buildGcpFooterHtml(input.ihcGeneralComment),
  };
}

export function resolveEffectiveClientReportIdentity(
  input: ResolveClientReportIdentityInput,
  serverIdentity?: ClientReportIdentity | null
): ClientReportIdentity {
  return serverIdentity || resolveClientReportIdentityFallback(input);
}
